// Taken in de tijd verzetten — gedeeld door Rooster, Dagstart-rooster en de uitklap van
// Capaciteit (Joris, 27 sep: "waarom is hier geen blokkenschema?"). Eén plek voor:
//  - wat een sleep op een dagcel betekent (verzettingUitDrop): een projectblok schuift
//    evenveel werkdagen, een losse taak begint op de gekozen dag en houdt zijn looptijd;
//  - het wegschrijven (schrijfVerzetting): PATCH /Api/tasks/:id met StartDateUitvoerder /
//    EndDateUitvoerder, één melding met "Ongedaan maken" (+ Cmd/Ctrl+Z) voor het geheel,
//    en een eerlijke melding als een deel niet lukte.
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { verplaatsUitkomst } from './rooster';
import { werkdagVerschil, schuifPeriode, verschuifNaar } from './plannerDatums';

const dag = (v) => String(v || '').slice(0, 10);

/** "2 taken 1 werkdag later gezet" — de melding na het verschuiven van een projectblok. */
export function blokMelding(aantal, delta) {
  const n = Math.abs(delta);
  return `${aantal} ${aantal === 1 ? 'taak' : 'taken'} ${n} werkdag${n === 1 ? '' : 'en'} ${delta > 0 ? 'later' : 'eerder'} gezet`;
}

/**
 * Wat een sleep op een dagcel betekent, in datums.
 * @param sleep  data van het gesleepte element ({ type: 'blok', tasks, bronPersoonId } of
 *               { type: 'task-bar', task: { …, bronPersoonId } })
 * @param doel   data van de dagcel ({ type: 'capacity-cell', personId, date })
 * @returns null (niets te doen) · { soort: 'collega' } (andere persoon — niet hier) ·
 *   { soort: 'blok', delta, taken: [{ taak, startDate, endDate, vorigeStart, vorigeEind }] } ·
 *   { soort: 'taak', taak, startDate, endDate }
 */
export function verzettingUitDrop(sleep, doel) {
  if (!sleep || doel?.type !== 'capacity-cell') return null;
  const bron = sleep.type === 'blok' ? sleep.bronPersoonId : sleep.task?.bronPersoonId;
  if (bron && bron !== doel.personId) return { soort: 'collega' };
  if (sleep.type === 'blok') {
    const taken = sleep.tasks || [];
    if (!taken.length) return null;
    const eerste = taken.map((t) => dag(t.startDate)).sort()[0];
    const delta = werkdagVerschil(eerste, doel.date);
    if (!delta) return null;
    return {
      soort: 'blok',
      delta,
      taken: taken.map((t) => {
        const s = dag(t.startDate);
        const e = dag(t.endDate || t.startDate);
        const nieuw = schuifPeriode(s, e, delta);
        return { taak: t, startDate: nieuw.start, endDate: nieuw.eind, vorigeStart: s, vorigeEind: e };
      }),
    };
  }
  if (sleep.type !== 'task-bar' || !sleep.task) return null;
  const taak = sleep.task;
  const s = dag(taak.startDate);
  if (s === doel.date) return null;
  const { start, eind } = verschuifNaar(s, taak.endDate, doel.date);
  return { soort: 'taak', taak, startDate: start, endDate: eind };
}

async function patchTaak(id, body) {
  const r = await authFetch(`${BACKEND_URL}/Api/tasks/${id}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`PATCH ${id}: ${r.status}`);
  return true;
}

/**
 * Een verzetting wegschrijven (optimistisch bijwerken doet de aanroeper vooraf).
 *
 * @param plan  [{ taak, velden, vorige }] — `velden` naar de server, `vorige` voor terugzetten
 * @param opties.melding      tekst van de ongedaan-melding als alles lukte
 * @param opties.foutMelding  melding als niets lukte (standaard die van verplaatsUitkomst)
 * @param opties.naGelukt     na elke geslaagde PATCH (planningcache ongeldig maken)
 * @param opties.bijMislukt   (mislukte plan-items) => void — vóór het herladen, om de
 *                            optimistische stand van die taken meteen terug te zetten
 * @param opties.bijTerug     (plan-items) => void — bij ongedaan maken, vóór de PATCHes
 * @param opties.herlaad      async () => void — na een (deels) mislukte actie en na terugzetten
 * @returns {Promise<{ gelukt: object[], mislukt: object[] }>}
 */
export async function schrijfVerzetting(plan, {
  melding, foutMelding, naGelukt, bijMislukt, bijTerug, herlaad,
} = {}) {
  const lijst = plan || [];
  if (!lijst.length) return { gelukt: [], mislukt: [] };
  const patch = async (id, body) => { await patchTaak(id, body); naGelukt?.(); return true; };
  const resultaten = await Promise.allSettled(lijst.map((p) => patch(p.taak.id, p.velden)));
  const { gelukt, mislukt, melding: deelMelding } = verplaatsUitkomst(lijst, resultaten);
  const herstel = async () => {
    bijTerug?.(gelukt);
    const terug = await Promise.allSettled(gelukt.map((p) => patch(p.taak.id, p.vorige)));
    await herlaad?.();
    const fout = terug.filter((r) => r.status !== 'fulfilled').length;
    if (fout) throw new Error(`${fout} van ${gelukt.length} taken niet teruggezet`);
  };
  if (!gelukt.length) {
    toast.error(foutMelding || deelMelding);
    bijMislukt?.(mislukt);
    await herlaad?.();
    return { gelukt, mislukt };
  }
  if (mislukt.length) {
    // Deels gelukt: de geslaagde zijn terug te zetten; pas nu herladen.
    biedOngedaanAan(deelMelding, herstel, { duur: 12000 });
    bijMislukt?.(mislukt);
    await herlaad?.();
    return { gelukt, mislukt };
  }
  biedOngedaanAan(melding, herstel);
  return { gelukt, mislukt };
}
